import { useRef, useState } from "react";
import { UploadCloud, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useBranding } from "@/lib/branding";
import { useConfirm } from "@/components/ConfirmDialog";

/**
 * Downscale an uploaded logo before shipping it to the backend.
 *
 * Rationale: any logo taller than ~120 px is wasted in the header (which
 * caps at 44 px), and admins tend to upload multi-megabyte source files.
 * Resize on the client so:
 *   - the payload sent to /admin/branding/logo stays well under 500 KB
 *   - the resulting logo looks crisp at header sizes on retina screens
 *   - SVGs are passed through as-is (they scale perfectly by nature)
 *
 * Target: `max` (default 240 px) is the longer edge — small enough to be
 * lightweight, large enough to look sharp at 44 px @ 2x DPR.
 */
async function resizeImageForLogo(file, { max = 240, mime = "image/png", quality = 0.92 } = {}) {
  // SVG stays vector — no raster resize needed. Just embed as-is.
  if (file.type === "image/svg+xml") {
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });
  }
  const objUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = objUrl;
    });
    const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, w, h);
    // Preserve transparency for PNG/WebP; JPEG stays JPEG.
    const outMime = file.type === "image/jpeg" ? "image/jpeg" : mime;
    return canvas.toDataURL(outMime, quality);
  } finally {
    URL.revokeObjectURL(objUrl);
  }
}

/**
 * Admin-only widget to upload / reset the app-wide logo (PNG / SVG / JPEG /
 * WebP, max ~500 KB). Uploaded logo is stored as a data-URL in
 * `system_meta.branding` and served publicly via `GET /api/branding`.
 * The <Logo /> component picks it up automatically via BrandingContext.
 */
export default function BrandingAdmin() {
  const { t } = useT();
  const { logoDataUrl, setLogoDataUrl, refresh } = useBranding();
  const confirm = useConfirm();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpeg|svg\+xml|webp)$/.test(file.type)) {
      toast.error(t("brndErrType"));
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await resizeImageForLogo(file);
      const { data } = await api.post("/admin/branding/logo", { logo_data_url: dataUrl });
      setLogoDataUrl(data.logo_data_url);
      toast.success(t("brndUploaded"));
      // Stripe forbids programmatic branding updates on the platform's own
      // account — surface a clear "one-time Dashboard step" reminder so the
      // logo also appears on Stripe invoices/receipts.
      if (data?.stripe_sync?.manual_dashboard_required && data.stripe_sync.file_id) {
        toast.info(t("brndStripeManual"), {
          duration: 15000,
          action: {
            label: t("brndStripeOpen"),
            onClick: () => window.open("https://dashboard.stripe.com/settings/branding", "_blank"),
          },
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("brndErrGeneric"));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    const ok = await confirm({
      title: t("brndResetConfirmTitle"),
      description: t("brndResetConfirmBody"),
      confirmText: t("brndResetConfirmCta"),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete("/admin/branding/logo");
      setLogoDataUrl(null);
      await refresh();
      toast.success(t("brndReset"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("brndErrGeneric"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-branding-section" className="max-w-2xl space-y-6">
      <div>
        <p className="ed-eyebrow mb-2">{t("brndTitle")}</p>
        <p className="text-sm text-neutral-600 leading-relaxed">{t("brndIntro")}</p>
      </div>

      <div className="ed-card p-6 flex items-start gap-6">
        <div
          data-testid="branding-preview"
          className="w-40 h-24 border border-neutral-300 bg-[#F9F8F6] flex items-center justify-center shrink-0 overflow-hidden"
        >
          {logoDataUrl ? (
            <img src={logoDataUrl} alt="Logo preview" className="max-h-full max-w-full" />
          ) : (
            <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
              {t("brndDefaultLabel")}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-neutral-700 mb-3 leading-relaxed">
            {logoDataUrl ? t("brndActive") : t("brndInactive")}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="branding-upload-btn"
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="ed-btn"
            >
              {busy
                ? <Loader2 size={13} strokeWidth={1.75} className="animate-spin" />
                : <UploadCloud size={13} strokeWidth={1.75} />}
              {logoDataUrl ? t("brndUploadReplace") : t("brndUpload")}
            </button>
            {logoDataUrl && (
              <button
                data-testid="branding-reset-btn"
                type="button"
                onClick={reset}
                disabled={busy}
                className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-300 text-neutral-700 hover:border-neutral-900 hover:text-neutral-900 px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <RotateCcw size={12} strokeWidth={1.75} /> {t("brndResetCta")}
              </button>
            )}
          </div>
          <p className="mt-3 text-xs font-mono text-neutral-500">{t("brndHint")}</p>
        </div>
      </div>

      <input
        ref={fileRef}
        data-testid="branding-file-input"
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/webp"
        onChange={onFile}
        className="hidden"
      />
    </div>
  );
}
